import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { bookmarksApi } from '@/api/endpoints/bookmarks';
import type { BookmarksResponse } from '@/api/types';
import { useToast } from '@/app/toasts/ToastProvider';
import { t } from '@/i18n/i18n';
import { useAuth } from '@/auth/AuthProvider';

export const bookmarksQueryKey = ['bookmarks'] as const;

/** Saved: множество id закладок; источник — сервер (mock до B1). */
export function useBookmarks() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: bookmarksQueryKey,
    queryFn: () => bookmarksApi.list(),
    enabled: isAuthenticated,
    select: (data) => new Set(data.video_ids),
  });
}

export function useBookmarkCount(): number {
  const { data } = useBookmarks();
  return data?.size ?? 0;
}

/** Тексты тостов — из прототипа AppV2 (словарь picker.*). */
const SAVED = () => t('picker.savedToast');
const REMOVED = () => t('picker.removedToast');

/** Закладки: оптимистичное обновление, откат при ошибке. */
export function useBookmarkMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const patchCache = useCallback(
    (videoId: number, on: boolean) => {
      queryClient.setQueryData<BookmarksResponse>(bookmarksQueryKey, (prev) => {
        const ids = prev?.video_ids ?? [];
        const next = on
          ? ids.includes(videoId)
            ? ids
            : [...ids, videoId]
          : ids.filter((id) => id !== videoId);
        return { video_ids: next };
      });
    },
    [queryClient],
  );

  const mutation = useMutation({
    mutationFn: ({
      videoId,
      on,
      searchEventId,
    }: {
      videoId: number;
      on: boolean;
      searchEventId?: number | null;
    }) => (on ? bookmarksApi.add(videoId, searchEventId) : bookmarksApi.remove(videoId)),
    onMutate: async ({ videoId, on }) => {
      await queryClient.cancelQueries({ queryKey: bookmarksQueryKey });
      const previous = queryClient.getQueryData<BookmarksResponse>(bookmarksQueryKey);
      patchCache(videoId, on);
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(bookmarksQueryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: bookmarksQueryKey });
      // В live закладки лежат на доске по умолчанию — список канвасов мог появиться или измениться.
      void queryClient.invalidateQueries({ queryKey: ['canvases'] });
    },
  });

  const setBookmark = useCallback(
    (
      videoId: number,
      on: boolean,
      options: { searchEventId?: number | null; silent?: boolean } = {},
    ) => {
      mutation.mutate({ videoId, on, searchEventId: options.searchEventId });
      if (!options.silent) toast.show(on ? SAVED() : REMOVED());
    },
    [mutation, toast],
  );

  const setMany = useCallback(
    (videoIds: number[], on: boolean) => {
      for (const id of videoIds) mutation.mutate({ videoId: id, on });
      if (videoIds.length) toast.show(on ? SAVED() : REMOVED());
    },
    [mutation, toast],
  );

  return { setBookmark, setMany, isPending: mutation.isPending };
}
